import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Skeleton,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TableFooter,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { sharePointService } from '../../services/sharePointService';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import { useSharePoint } from '../../contexts/SharePointContext';
import { useTheme } from '@mui/material/styles';
import { siteStatsDebug } from '../../utils/siteStatsDebug';
import { useFetchGeneration } from '../../hooks/useFetchGeneration';

const BRAND_COLOR = '#e65100';
const BRAND_COLOR_FADED = '#ff9d57'; // Even lighter/more faded version of the brand color

const StatCard = ({ title, value, loading, tooltip }) => (
  <Paper 
    elevation={3} 
    sx={{ 
      p: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6
      }
    }}
  >
    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
      <Tooltip title={tooltip} arrow>
        <IconButton size="small">
          <InfoIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
    </Box>
    <Typography variant="h6" color="primary" gutterBottom>
      {title}
    </Typography>
    {loading ? (
      <Skeleton width={100} height={40} />
    ) : (
      <Typography variant="h4" component="div">
        {value}
      </Typography>
    )}
  </Paper>
);

const SharePointStats = () => {
  const theme = useTheme();
  const { userSession } = useUserSession();
  const { effectiveConfigId, configurations, loading: configLoading, refreshTrigger } = useSharePoint();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [etlLoading, setEtlLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allSites, setAllSites] = useState(false);
  const [etlStatus, setEtlStatus] = useState([]);
  const { begin: beginStatsFetch, isCurrent: isStatsFetchCurrent } = useFetchGeneration();
  const { begin: beginEtlFetch, isCurrent: isEtlFetchCurrent } = useFetchGeneration();

  useEffect(() => {
    const fetchStats = async () => {
      if (!userSession?.email) {
        setError('User email not available');
        setStatsLoading(false);
        return;
      }
      const fetchGen = beginStatsFetch();
      const requestedConfigId = effectiveConfigId;
      siteStatsDebug('StatsFetch', {
        event: 'START',
        fetchGen,
        effectiveConfigId: requestedConfigId,
        impersonatedConfigId: userSession?.impersonatedConfigId ?? null,
        refreshTrigger,
        configLoading,
        allSites,
      });
      try {
        setStatsLoading(true);
        setError(null);
        const token = authApi.getToken();
        let response;
        if (allSites) {
          const aggStats = await sharePointService.getSharePointStats(token);
          if (!isStatsFetchCurrent(fetchGen)) return;
          const totalUsers = aggStats.totalUsers;
          const statsList = await Promise.all(
            configurations.map((cfg) => sharePointService.getSharePointStats(token, cfg.id))
          );
          if (!isStatsFetchCurrent(fetchGen)) return;
          const totalLibraries = statsList.reduce((sum, st) => sum + (st.totalLibraries || 0), 0);
          const totalDocuments = statsList.reduce((sum, st) => sum + (st.totalDocuments || 0), 0);
          const combinedDetails = [];
          statsList.forEach((st, idx) => {
            const cfg = configurations[idx];
            const prefix = cfg.tenant_url.replace(/^https?:\/\//, '');
            (st.libraryDetails || []).forEach(lib => {
              combinedDetails.push({
                ...lib,
                libraryName: `${prefix} / ${lib.libraryName}`,
              });
            });
          });
          response = { totalLibraries, totalDocuments, totalUsers, libraryDetails: combinedDetails };
        } else {
          response = await sharePointService.getSharePointStats(token, requestedConfigId);
          if (!isStatsFetchCurrent(fetchGen)) return;
        }
        setStats(response);
        siteStatsDebug('StatsFetch', {
          event: 'DONE',
          fetchGen,
          configId: requestedConfigId,
          totalLibraries: response?.totalLibraries ?? null,
          totalDocuments: response?.totalDocuments ?? null,
          libraryDetailCount: response?.libraryDetails?.length ?? 0,
        });
      } catch (err) {
        if (isStatsFetchCurrent(fetchGen)) {
          setError(err.message || 'Failed to fetch statistics');
        }
      } finally {
        if (isStatsFetchCurrent(fetchGen)) {
          setStatsLoading(false);
        }
      }
    };
    if (configLoading || !userSession?.email || (!effectiveConfigId && !allSites)) {
      return;
    }
    fetchStats();
  }, [userSession?.email, userSession?.effectiveEmail, effectiveConfigId, allSites, configurations, configLoading, refreshTrigger, beginStatsFetch, isStatsFetchCurrent]);

  // Fetch ETL status independently
  useEffect(() => {
    const fetchEtlStatus = async () => {
      if (configLoading || !userSession?.email || !effectiveConfigId) return;
      const fetchGen = beginEtlFetch();
      setEtlLoading(true);
      try {
        const token = authApi.getToken();
        const etlData = await sharePointService.getEtlStatus(token, effectiveConfigId);
        if (isEtlFetchCurrent(fetchGen)) {
          setEtlStatus(etlData);
        }
      } catch (err) {
        console.error('Error loading ETL status:', err);
      } finally {
        if (isEtlFetchCurrent(fetchGen)) {
          setEtlLoading(false);
        }
      }
    };
    fetchEtlStatus();
  }, [userSession?.email, userSession?.effectiveEmail, effectiveConfigId, configLoading, refreshTrigger, beginEtlFetch, isEtlFetchCurrent]);

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading SharePoint statistics: {error}
        </Alert>
      </Box>
    );
  }

  const permissionScopeTo = useMemo(() => {
    if (allSites) return '/permission-scope-report?allSites=1';
    if (effectiveConfigId != null) {
      return `/permission-scope-report?configId=${encodeURIComponent(effectiveConfigId)}`;
    }
    return null;
  }, [allSites, effectiveConfigId]);

  // Add new column to the table
  const columns = [
    { field: 'libraryName', headerName: 'Library Name', flex: 1 },
    { field: 'fileCount', headerName: 'Files', width: 100 },
    { field: 'folderCount', headerName: 'Folders', width: 100 },
    { field: 'topContributor', headerName: 'Top Contributor', flex: 1.2 }
  ];

  // Update the rows mapping to include all required fields
  const rows = stats?.libraryDetails?.map((lib, index) => ({
    id: index,
    libraryName: lib.libraryName,
    fileCount: lib.fileCount || 0,
    versionCount: lib.versionCount || 0,
    folderCount: lib.folderCount || 0,
    created30d: lib.created30d || 0,
    created90d: lib.created90d || 0,
    created365d: lib.created365d || 0,
    modified30d: lib.modified30d || 0,
    modified90d: lib.modified90d || 0,
    modified365d: lib.modified365d || 0,
    topContributor: lib.topContributor || 'N/A'
  })) || [];

  return (
    <Box sx={{ mt: 8 }}>
      <Typography variant="h4" gutterBottom>
        Overview
      </Typography>
      {configurations && configurations.length > 1 && (
        <FormControlLabel
          control={
            <Checkbox checked={allSites} onChange={e => setAllSites(e.target.checked)} color="primary" />
          }
          label="All Sites"
          sx={{ mb: 2 }}
        />
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Libraries', value: stats?.totalLibraries },
          { label: 'Documents', value: stats?.totalDocuments },
          {
            label: 'Unique Permissions',
            value: stats?.totalUsers,
            linkTo: permissionScopeTo,
            usersTooltip:
              'Distinct principals from library defaults (once per library) plus explicit grants; excludes [REMOVED] and DEFAULT rows; includes groups. Not your full directory. Opens the Permission scope report.'
          }
        ].map((item, index) => {
          const card = (
            <Paper
              component={item.linkTo ? RouterLink : 'div'}
              {...(item.linkTo ? { to: item.linkTo } : {})}
              elevation={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 1.5,
                px: 2,
                backgroundColor: BRAND_COLOR_FADED,
                transition: 'transform 0.2s ease-in-out',
                textDecoration: 'none',
                cursor: item.linkTo ? 'pointer' : 'default',
                color: 'inherit',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  mb: 0.5
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: 'white'
                }}
              >
                {statsLoading ? '-' : (item.value != null ? item.value.toLocaleString() : '0')}
              </Typography>
            </Paper>
          );
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              {item.usersTooltip ? (
                <Tooltip title={item.usersTooltip} placement="top">
                  <Box component="span" sx={{ display: 'block' }}>
                    {card}
                  </Box>
                </Tooltip>
              ) : (
                card
              )}
            </Grid>
          );
        })}
      </Grid>

      {/* Library Details Table */}
      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={32} />
        </Box>
      ) : stats?.libraryDetails && (
        <TableContainer component={Paper} sx={{ 
          mt: 0,
          '& .MuiTableCell-root': {
            py: 1,
            px: 2,
            fontSize: '0.875rem'
          }
        }}>
          <Table aria-label="library details table" size="small" sx={{ 
            '& .MuiTableCell-root:not(thead .MuiTableCell-root)': {
              '&:last-child': {
                fontWeight: 500,
                color: theme.palette.primary.dark
              }
            }
          }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4511e' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Library Name</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Folders</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Files(Versions)</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }} colSpan={3}>Created Files</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }} colSpan={3}>Modified Files</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }} rowSpan={2}>Top Contributor</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f4511e' }}>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>30</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>90</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>365</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>30</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>90</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>365</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                    {row.libraryName}
                  </TableCell>
                  <TableCell align="right">{row.folderCount.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {row.fileCount.toLocaleString()} ({row.versionCount.toLocaleString()})
                  </TableCell>
                  <TableCell align="right">{row.created30d}</TableCell>
                  <TableCell align="right">{row.created90d}</TableCell>
                  <TableCell align="right">{row.created365d}</TableCell>
                  <TableCell align="right">{row.modified30d}</TableCell>
                  <TableCell align="right">{row.modified90d}</TableCell>
                  <TableCell align="right">{row.modified365d}</TableCell>
                  <TableCell align="right">{row.topContributor}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: '#f5f5f5' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.folderCount, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {`${(stats.libraryDetails || []).reduce((sum, lib) => sum + lib.fileCount, 0).toLocaleString()} (${(stats.libraryDetails || []).reduce((sum, lib) => sum + lib.versionCount, 0).toLocaleString()})`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.created30d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.created90d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.created365d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.modified30d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.modified90d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(stats?.libraryDetails || []).reduce((sum, row) => sum + row.modified365d, 0)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      {/* ETL Status Summary Table */}
      {etlLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={32} />
        </Box>
      ) : etlStatus.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            ETL Status
          </Typography>
          <TableContainer component={Paper} size="small" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4511e' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Library Name</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Tooltip title="Format: Processed (Pending) - Shows number of external shares already processed and number still pending" arrow>
                      <span>External Shares Pending</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Tooltip title="Format: Processed (Pending) - Shows number of analytics already processed and number still pending" arrow>
                      <span>Analytics Pending</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Tooltip title="Format: Processed (Pending) - Shows number of permissions already processed and number still pending" arrow>
                      <span>Permissions Pending</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {etlStatus.map((etlRow) => {
                  const statRow = rows.find(r => r.libraryName === etlRow.libraryName || r.id === etlRow.libraryId); // Match by name or ID
                  const totalFileCount = statRow ? statRow.fileCount : 0;

                  const externalSharesPending = etlRow.externalSharesPending || 0;
                  const externalSharesProcessed = Math.max(0, totalFileCount - externalSharesPending);

                  const analyticsPending = etlRow.analyticsPending || 0;
                  const analyticsProcessed = Math.max(0, totalFileCount - analyticsPending);

                  const permissionsPending = etlRow.permissionsPending || 0;
                  const permissionsProcessed = Math.max(0, totalFileCount - permissionsPending);

                  return (
                    <TableRow key={etlRow.libraryId}>
                      <TableCell>{etlRow.libraryName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Processed (Pending) - Number of external shares processed and number still pending processing" arrow>
                          <span>{`${externalSharesProcessed.toLocaleString()} (${externalSharesPending.toLocaleString()})`}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Processed (Pending) - Number of analytics processed and number still pending processing" arrow>
                          <span>{`${analyticsProcessed.toLocaleString()} (${analyticsPending.toLocaleString()})`}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Processed (Pending) - Number of permissions processed and number still pending processing" arrow>
                          <span>{`${permissionsProcessed.toLocaleString()} (${permissionsPending.toLocaleString()})`}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: '#f5f5f5' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Tooltip title="Total Processed (Total Pending) - Total external shares processed and total still pending processing" arrow>
                      <span>
                        {(() => {
                          const totalProcessed = etlStatus.reduce((sum, etlRow) => {
                            const statRow = rows.find(r => r.libraryName === etlRow.libraryName || r.id === etlRow.libraryId);
                            const totalFileCount = statRow ? statRow.fileCount : 0;
                            const pending = etlRow.externalSharesPending || 0;
                            return sum + Math.max(0, totalFileCount - pending);
                          }, 0);
                          const totalPending = etlStatus.reduce((sum, etlRow) => sum + (etlRow.externalSharesPending || 0), 0);
                          return `${totalProcessed.toLocaleString()} (${totalPending.toLocaleString()})`
                        })()}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Tooltip title="Total Processed (Total Pending) - Total analytics processed and total still pending processing" arrow>
                      <span>
                        {(() => {
                          const totalProcessed = etlStatus.reduce((sum, etlRow) => {
                            const statRow = rows.find(r => r.libraryName === etlRow.libraryName || r.id === etlRow.libraryId);
                            const totalFileCount = statRow ? statRow.fileCount : 0;
                            const pending = etlRow.analyticsPending || 0;
                            return sum + Math.max(0, totalFileCount - pending);
                          }, 0);
                          const totalPending = etlStatus.reduce((sum, etlRow) => sum + (etlRow.analyticsPending || 0), 0);
                          return `${totalProcessed.toLocaleString()} (${totalPending.toLocaleString()})`
                        })()}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Tooltip title="Total Processed (Total Pending) - Total permissions processed and total still pending processing" arrow>
                      <span>
                        {(() => {
                          const totalProcessed = etlStatus.reduce((sum, etlRow) => {
                            const statRow = rows.find(r => r.libraryName === etlRow.libraryName || r.id === etlRow.libraryId);
                            const totalFileCount = statRow ? statRow.fileCount : 0;
                            const pending = etlRow.permissionsPending || 0;
                            return sum + Math.max(0, totalFileCount - pending);
                          }, 0);
                          const totalPending = etlStatus.reduce((sum, etlRow) => sum + (etlRow.permissionsPending || 0), 0);
                          return `${totalProcessed.toLocaleString()} (${totalPending.toLocaleString()})`
                        })()}
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default SharePointStats; 